import { createContext, useContext, useCallback, useState, useEffect, type ReactNode } from 'react';
import type { Listing } from '../api/client';

const STORAGE_KEY = 'darlink_saved_listings';

export type SavedListing = Pick<
  Listing,
  'listingId' | 'titleEn' | 'price' | 'currency' | 'location' | 'type' | 'mediaKeys' | 'bedrooms' | 'bathrooms' | 'areaSqm'
>;

function loadSaved(): SavedListing[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(items: SavedListing[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

type SavedContextValue = {
  saved: SavedListing[];
  count: number;
  isSaved: (listingId: string) => boolean;
  add: (listing: Listing | SavedListing) => void;
  remove: (listingId: string) => void;
  toggle: (listing: Listing | SavedListing) => void;
};

const SavedListingsContext = createContext<SavedContextValue | null>(null);

function toSavedListing(l: Listing | SavedListing): SavedListing {
  return {
    listingId: l.listingId,
    titleEn: l.titleEn,
    price: l.price,
    currency: l.currency,
    location: l.location,
    type: l.type,
    mediaKeys: l.mediaKeys,
    bedrooms: l.bedrooms,
    bathrooms: l.bathrooms,
    areaSqm: l.areaSqm,
  };
}

export function SavedListingsProvider({ children }: { children: ReactNode }) {
  const [saved, setSaved] = useState<SavedListing[]>(() => loadSaved());

  useEffect(() => {
    saveToStorage(saved);
  }, [saved]);

  const isSaved = useCallback(
    (listingId: string) => saved.some((s) => s.listingId === listingId),
    [saved]
  );

  const add = useCallback((listing: Listing | SavedListing) => {
    setSaved((prev) => {
      const item = toSavedListing(listing);
      if (prev.some((s) => s.listingId === item.listingId)) return prev;
      return [...prev, item];
    });
  }, []);

  const remove = useCallback((listingId: string) => {
    setSaved((prev) => prev.filter((s) => s.listingId !== listingId));
  }, []);

  const toggle = useCallback((listing: Listing | SavedListing) => {
    setSaved((prev) => {
      const item = toSavedListing(listing);
      const exists = prev.some((s) => s.listingId === item.listingId);
      if (exists) return prev.filter((s) => s.listingId !== item.listingId);
      return [...prev, item];
    });
  }, []);

  const value: SavedContextValue = {
    saved,
    count: saved.length,
    isSaved,
    add,
    remove,
    toggle,
  };

  return (
    <SavedListingsContext.Provider value={value}>
      {children}
    </SavedListingsContext.Provider>
  );
}

export function useSavedListings(): SavedContextValue {
  const ctx = useContext(SavedListingsContext);
  if (!ctx) throw new Error('useSavedListings must be used within SavedListingsProvider');
  return ctx;
}
